import { useSearchParams } from 'react-router-dom'
import { CHART_SURFACE, IDENTITY_COLORS, identityFor, type Mark } from '../../Component/Charts/Options/visualIdentity.js'
import { IdentityKey } from '../../Component/Charts/IdentityKey.js'

const parseNames = (raw: string | null): string[] =>
  [...new Set((raw ?? '').split(',').map((n) => n.trim()).filter(Boolean))]

const MARK_LABEL: Record<Mark, string> = { solid: 'solid', bands: 'dashed', stagger: 'dotted', hatch: 'hatched' }

/** `/colorcode?name=Insecta` — the visual identity a name wears in every chart. */
export const ColorCodePage = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const raw = searchParams.get('name') ?? ''
  const names = parseNames(raw)

  return (
    <div className="max-w-3xl mx-auto p-4 space-y-6">
      <header>
        <h1 className="text-3xl font-bold">Colour code</h1>
        <p className="text-sm text-gray-600 mt-2">
          The colour and pattern a taxon or trait wears in the visualisation charts. It depends on the name alone, so it is the same in
          every chart and project. Separate several names with commas.
        </p>
      </header>

      <input
        className="input input-bordered w-full"
        placeholder="Insecta, Arachnida, Collembola"
        value={raw}
        onChange={(e) => setSearchParams(e.target.value ? { name: e.target.value } : {}, { replace: true })}
        aria-label="Names"
      />

      {names.length > 0 && (
        <div className="rounded-2xl p-6 flex flex-wrap gap-6" style={{ backgroundColor: CHART_SURFACE }}>
          {names.map((name) => {
            const identity = identityFor(name)
            return (
              <figure key={name} className="flex flex-col items-center gap-2 w-40">
                <div className="relative w-40 h-40 rounded-2xl shadow-lg ring-1 ring-black/10 overflow-hidden" data-testid="color-square">
                  <span className="absolute inset-0">
                    <IdentityKey name={name} size={160} lineLength={0} />
                  </span>
                  <span className="absolute left-2 bottom-2 right-2 rounded-md bg-white/85 px-2 py-1 text-sm font-semibold text-gray-900 break-all leading-tight">
                    {name}
                  </span>
                </div>
                <div className="flex flex-col items-center gap-1">
                  {[1.5, 2.5, 4].map((w) => (
                    <IdentityKey key={w} name={name} size={0} lineLength={120} lineWidth={w} />
                  ))}
                </div>
                <figcaption className="text-xs text-gray-700 text-center font-mono">
                  {identity.reserved
                    ? `${identity.color} · reserved`
                    : `${identity.color} · colour ${IDENTITY_COLORS.indexOf(identity.color) + 1}/${IDENTITY_COLORS.length} · ${MARK_LABEL[identity.mark]}`}
                </figcaption>
              </figure>
            )
          })}
        </div>
      )}
    </div>
  )
}
